<template>
    <button
        :disabled="disabled"
        @click="() => onClick()"
        class="font-semibold rounded-md py-2 px-4"
        :class="[buttonStyle]"
    >
        <slot></slot>
    </button>
</template>

<script setup lang="ts">
import ClickSound from '@/assets/mp3/simple_click.mp3';
import useSettingsStore from '~/stores/settings';
import { safeHtmlAudioPlay } from '~/utils/sound';

const props = defineProps({
    mode: {
        type: String as PropType<ButtonMode>,
        default: '',
    },
    sound: {
        type: Boolean,
        default: void 0, // 这里不使用 false 做为默认，以便于识别是否有传入值 
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

let oAudio = (
    import.meta.client
    ? new Audio(ClickSound)
    : null
);

const settingStore = useSettingsStore();

const onClick = () => {
    // console.log('props.sound', props.sound);
    const toPlay = (
        typeof props.sound == 'boolean'
        ? props.sound
        : !!settingStore.globalSounds
    );
    if (!toPlay) return;
    if (!oAudio) return;
    oAudio.currentTime = 0;
    safeHtmlAudioPlay(oAudio);
};

onMounted(() => {
    // 以 play() 执行之前已经设为 0 
    // oAudio.addEventListener('ended', () => {
    //     oAudio.currentTime = 0;
    // });
});

const buttonStyle = computed(() => {
    switch (props.mode) {
        case 'primary':
            return 'button-blue text-white';
        case 'primaryContained':
            return 'primaryContained text-white';
        case 'primaryContainedShadow':
            return 'primaryContainedShadow text-white';
        case 'secondary':
            return 'bg-btnSecondary text-white';
        case 'secondaryContained':
            return 'secondaryContained text-white';
        case 'secondaryContainedShadow':
            return 'secondaryContainedShadow text-white';
        case 'normal':
            return 'text-[#55657e] hover:bg-[rgba(255,255,255,0.1)] hover:text-white';
        case 'normalContained':
            return 'bg-[rgba(255,255,255,0.1)] text-[#55657e]';
        case 'grayContained':
            return 'bg-grey-60 text-grey-20 hover:bg-grey-70 ';
        default:
            return 'text-[#55657e] ';
    }
});
</script>

<script lang="ts">
export default {
    name: 'TabButton',
};
export type ButtonMode =
    | 'primary'
    | 'secondary'
    | 'normal'
    | 'normalContained'
    | 'primaryContained'
    | 'secondaryContained'
    | 'primaryContainedShadow'
    | 'secondaryContainedShadow'
    | 'grayContained';
</script>

<style scoped lang="scss">
.primaryContained {
    background: linear-gradient(1turn, #0c5eff 0.8%, #1398f9);
    box-shadow: 0 3px 28px #2283f666, inset 0 3px 3px #ffffff4d;
    border-radius: 8px;
    position: relative;
    padding: 6.5px 24px;
}
.primaryContainedShadow {
    transition: all 0.3s;
    background: linear-gradient(1turn, #0c5eff 0.8%, #1398f9);
    box-shadow: 0 3px 28px rgba(34, 131, 246, 0.4), inset 0 3px 3px rgba(255, 255, 255, 0.3019607843);
}
.primaryContainedShadow:hover {
    box-shadow: 0 14px 20px rgba(34, 131, 246, 0.3);
    background-color: #0a75f5;
}

.secondaryContained {
    background: linear-gradient(1turn, #c4003b 0.8%, #fb1949);
    box-shadow: 0 3px 16px #ff234180, inset 0 4px 3px #ffffff4d;
    border-radius: 8px;
    position: relative;
}

.secondaryContainedShadow {
    transition: all 0.3s;
    background: linear-gradient(1turn, #c4003b 0.8%, #fb1949);
    box-shadow: 0 3px 16px rgba(255, 35, 65, 0.5019607843), inset 0 4px 3px rgba(255, 255, 255, 0.3019607843);
}
.secondaryContainedShadow:hover {
    box-shadow: 0 14px 20px rgba(237, 29, 73, 0.3);
    background-color: #df123d;
}

.button {
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}
</style>
